<template>
  <el-dialog
    :visible.sync="visible"
    :title="`Duplicate Analysis - ${track && track.title ? track.title : ''}`"
    width="900px"
    custom-class="premium-modal duplicate-modal"
    @close="$emit('close')"
  >
    <!-- Loading spinner -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading loading-spinner"></i>
      <p>Loading duplicates...</p>
    </div>

    <div v-else-if="track && duplicates.length > 0" class="duplicate-content">
      <!-- Navigation arrows -->
      <div class="duplicate-navigation">
        <el-button
          class="nav-arrow nav-arrow-left"
          @click="previousDuplicate"
          :disabled="currentDuplicateIndex === 0"
          circle
          icon="el-icon-arrow-left">
        </el-button>
        <span class="duplicate-counter">{{ currentDuplicateIndex + 1 }} / {{ duplicates.length }}</span>
        <el-button
          class="nav-arrow nav-arrow-right"
          @click="nextDuplicate"
          :disabled="currentDuplicateIndex === duplicates.length - 1"
          circle
          icon="el-icon-arrow-right">
        </el-button>
      </div>
      <div class="duplicate-header">
        <div class="original-track">
          <h4>Reference Track</h4>
          <p>{{ track.title }}</p>
          <small>ID: {{ track.id }}</small>
        </div>
        <div class="similarity-indicator">
          <div class="similarity-circle">
            <svg viewBox="0 0 36 36" class="circular-chart">
              <path class="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" stroke-width="3" fill="none" />
              <path class="circle" :stroke-dasharray="`${currentDuplicate.similarity}, 100`"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" stroke-width="3" fill="none"
              />
            </svg>
            <span class="percentage">{{ Math.round(currentDuplicate.similarity) }}%</span>
          </div>
          <p>Similarity</p>
        </div>
        <div class="duplicate-track">
          <h4>Duplicate Track</h4>
          <p>{{ currentDuplicate.duplicate_track ? currentDuplicate.duplicate_track.name : (currentDuplicate.title || '') }}</p>
          <small>ID: {{ currentDuplicate.duplicate_track_id || currentDuplicate.id || '' }}</small>
        </div>
      </div>

      <div v-if="+currentDuplicate.similarity !== 100" class="segments-section">
        <h4>Segment Comparison</h4>
        <div class="segments-info">
          <span class="info-label"><i class="el-icon-info"></i> Click left side for Reference Track, right side for Duplicate Track</span>
        </div>
        <div class="segments-timeline">
          <div class="segment-item-combined" v-for="(segment, index) in combinedSegments" :key="index">
            <div class="segment-left" @click="playSegment(segment.original, 'current', index)"
                 :class="{
                   active: activeSegment === index && activePlayer === 'current',
                   disabled: !segment.original
                 }">
              <i class="el-icon-headset"></i>
              <span>{{ segment.original || 'N/A' }}s</span>
            </div>
            <div class="segment-divider"> <span class="vs-label">vs</span> </div>
            <div class="segment-right" @click="playSegment(segment.duplicate, 'duplicate', index)"
                 :class="{
                   active: activeSegment === index && activePlayer === 'duplicate',
                   disabled: !segment.duplicate
                 }">
              <span>{{ segment.duplicate || 'N/A' }}s</span>
              <i class="el-icon-copy-document"></i>
            </div>
          </div>
        </div>
      </div>

      <div class="player-section" v-if="showPlayer">
        <h4>Audio Players</h4>

        <!-- Current Track Player -->
        <div class="player-wrapper">
          <h5><i class="el-icon-headset"></i> Reference Track</h5>
          <div class="player-controls">
            <el-button-group>
              <el-button @click="playFullTrack" type="primary">
                <i class="el-icon-video-play"></i> Play Full Track
              </el-button>
              <el-button @click="playOnlyDuplicates" type="warning">
                <i class="el-icon-s-operation"></i> Play Only Duplicates
              </el-button>
            </el-button-group>
          </div>
          <audio ref="audioPlayer" controls style="width: 100%; margin-top: 20px;">
            <source :src="audioSource" type="audio/wav">
          </audio>
          <div class="segment-highlight" v-if="playingSegments">
            <el-tag type="warning">Playing: {{ currentSegmentDisplay }}</el-tag>
          </div>
        </div>

        <!-- Duplicate Track Player -->
        <div class="player-wrapper duplicate-player" v-if="currentDuplicate && currentDuplicate.path">
          <h5><i class="el-icon-copy-document"></i> Duplicate Track</h5>
          <audio ref="duplicateAudioPlayer" controls style="width: 100%; margin-top: 10px;">
            <source :src="host + '/' + currentDuplicate.path" type="audio/wav">
          </audio>
        </div>
      </div>     
    </div>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'DuplicateDetailsModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    track: {
      type: Object,
      default: null
    },
    audioSource: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      showPlayer: true,
      playingSegments: false,
      activeSegment: null,
      activePlayer: null,
      currentSegmentDisplay: '',
      currentDuplicateIndex: 0,
      loading: false,
      host: process.env.VUE_APP_API_HOST
    }
  },

  computed: {
    ...mapState('distro', ['duplicates']),

    currentDuplicate() {
      if (this.duplicates && this.duplicates.length > 0) {
        return this.duplicates[this.currentDuplicateIndex]
      }
      return null
    },

    parsedSegments() {
      if (!this.currentDuplicate || !this.currentDuplicate.segments) return []
      return this.currentDuplicate.segments
    },
    parsedOriginalSegments() {
      if (!this.currentDuplicate || !this.currentDuplicate.original_segments) return []
      return this.currentDuplicate.original_segments
    },
    combinedSegments() {
      const maxLength = Math.max(this.parsedOriginalSegments.length, this.parsedSegments.length)
      const combined = []

      for (let i = 0; i < maxLength; i++) {
        combined.push({
          original: this.parsedOriginalSegments[i] || null,
          duplicate: this.parsedSegments[i] || null
        })
      }
      return combined
    }
  },

  methods: {
    playSegment(segment, player, index) {
      if (!segment) return

      // Pause the other player first
      if (player === 'duplicate' && this.$refs.audioPlayer) {
        this.$refs.audioPlayer.pause()
      } else if (player === 'current' && this.$refs.duplicateAudioPlayer) {
        this.$refs.duplicateAudioPlayer.pause()
      }

      const audioPlayer = player === 'duplicate' ? this.$refs.duplicateAudioPlayer : this.$refs.audioPlayer
      if (!audioPlayer) return;

      const [start, end] = segment.split('-').map(Number)
      
      audioPlayer.currentTime = start
      audioPlayer.play()

      this.activeSegment = index
      this.activePlayer = player
      this.currentSegmentDisplay = `${segment} (${player === 'duplicate' ? 'Duplicate' : 'Current'})`

      // Auto stop at segment end
      const checkTime = setInterval(() => {
        if (audioPlayer.currentTime >= end) {
          audioPlayer.pause()
          clearInterval(checkTime)
          this.activeSegment = null
          this.activePlayer = null
        }
      }, 100)
    },

    playFullTrack() {
      if (!this.$refs.audioPlayer) return
      // Pause duplicate player if playing
      if (this.$refs.duplicateAudioPlayer) {
        this.$refs.duplicateAudioPlayer.pause()
      }
      this.$refs.audioPlayer.currentTime = 0
      this.$refs.audioPlayer.play()
      this.playingSegments = false
    },

    playOnlyDuplicates() {
      if (!this.$refs.audioPlayer || !this.parsedSegments.length) return

      // Pause duplicate player if playing
      if (this.$refs.duplicateAudioPlayer) {
        this.$refs.duplicateAudioPlayer.pause()
      }

      this.playingSegments = true
      let segmentIndex = 0

      const playNextSegment = () => {
        if (segmentIndex >= this.parsedSegments.length) {
          this.playingSegments = false
          return
        }

        const segment = this.parsedSegments[segmentIndex]
        this.currentSegmentDisplay = segment
        this.playSegment(segment, 'current', segmentIndex)

        segmentIndex++

        // Wait for segment to finish then play next
        const [start, end] = segment.split('-').map(Number)
        setTimeout(() => {
          playNextSegment()
        }, (end - start) * 1000 + 500)
      }

      playNextSegment()
    },

    previousDuplicate() {
      if (this.currentDuplicateIndex > 0) {
        this.currentDuplicateIndex--
        this.resetPlayers()
      }
    },

    nextDuplicate() {
      if (this.currentDuplicateIndex < this.duplicates.length - 1) {
        this.currentDuplicateIndex++
        this.resetPlayers()
      }
    },

    resetPlayers() {
      // Pause and reset both players
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.pause()
        this.$refs.audioPlayer.currentTime = 0
      }
      if (this.$refs.duplicateAudioPlayer) {
        this.$refs.duplicateAudioPlayer.pause()
        this.$refs.duplicateAudioPlayer.currentTime = 0
        // Force reload the audio source
        this.$refs.duplicateAudioPlayer.load()
      }
      // Reset states
      this.activeSegment = null
      this.activePlayer = null
      this.playingSegments = false
    },

    async fetchDuplicates() {
      if (this.track) {
        this.loading = true
        try {
          await this.$store.dispatch('distro/duplicates', this.track)
          this.currentDuplicateIndex = 0
        } catch (error) {
          console.error('Error fetching duplicates:', error)
        } finally {
          this.loading = false
        }
      }
    }
  },

  watch: {
    visible(val) {
      if (val && this.track) {
        // Fetch duplicates when modal opens
        this.fetchDuplicates()
      } else if (!val) {
        // Reset audio players when modal closes
        if (this.$refs.audioPlayer) {
          this.$refs.audioPlayer.pause()
          this.$refs.audioPlayer.currentTime = 0
        }
        if (this.$refs.duplicateAudioPlayer) {
          this.$refs.duplicateAudioPlayer.pause()
          this.$refs.duplicateAudioPlayer.currentTime = 0
        }
        // Reset states
        this.playingSegments = false
        this.activeSegment = null
        this.activePlayer = null
        this.currentSegmentDisplay = ''
        this.currentDuplicateIndex = 0

        this.$emit('update:visible', false)
        this.$emit('close')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Premium Modal Styles
.premium-modal {
  ::v-deep .el-dialog__header {
    background: linear-gradient(135deg, #e55279 0%, #9b6dbb 100%);
    padding: 20px;

    .el-dialog__title {
      color: white;
      font-size: 18px;
      font-weight: 600;
    }

    .el-dialog__close {
      color: white;
      font-size: 20px;

      &:hover {
        color: #f0f0f0;
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 30px;
  }
}

// Duplicate Modal Specific Styles
.duplicate-modal {
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    min-height: 200px;

    .loading-spinner {
      font-size: 48px;
      color: #667eea;
      animation: rotate 1.5s linear infinite;
    }

    p {
      margin-top: 20px;
      font-size: 16px;
      color: #868e96;
      font-weight: 500;
    }
  }

  .duplicate-content {
    .duplicate-navigation {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20px;
      margin-bottom: 20px;
      padding: 15px;
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border-radius: 10px;

      .nav-arrow {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border: none;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;

        &:hover:not(.is-disabled) {
          background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
          transform: scale(1.1);
        }

        &.is-disabled {
          background: #e9ecef;
          color: #adb5bd;
          cursor: not-allowed;
        }
      }

      .duplicate-counter {
        font-size: 16px;
        font-weight: 600;
        color: #495057;
        min-width: 80px;
        text-align: center;
        background: white;
        padding: 8px 16px;
        border-radius: 20px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      }
    }

    .duplicate-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 25px;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-radius: 12px;
      margin-bottom: 30px;

      .original-track,
      .duplicate-track {
        text-align: center;

        h4 {
          margin: 0 0 10px 0;
          color: #495057;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        p {
          margin: 0 0 5px 0;
          font-weight: 600;
          color: #212529;
          font-size: 16px;
        }

        small {
          color: #868e96;
          font-size: 11px;
        }
      }

      .similarity-indicator {
        text-align: center;

        .similarity-circle {
          width: 110px;
          height: 110px;
          position: relative;
          margin: 0 auto 10px;

          .circular-chart {
            display: block;
            margin: 0 auto;
            max-width: 100%;
            max-height: 110px;
          }

          .circle-bg {
            stroke: #f0f2f5;
          }

          .circle {
            stroke: #f093fb;
            stroke-linecap: round;
            animation: progress 1s ease-out forwards;
          }

          .percentage {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 18px;
            font-weight: bold;
            color: #667eea;
            white-space: nowrap;
          }
        }

        p {
          margin: 0;
          font-size: 12px;
          color: #868e96;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
      }
    }

    .segments-section {
      margin-bottom: 30px;

      h4 {
        margin: 0 0 15px 0;
        color: #495057;
        font-size: 16px;
        text-transform: uppercase;
        letter-spacing: 1px;
        text-align: center;
        position: relative;

        &:after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 60px;
          height: 3px;
          background: linear-gradient(135deg, #667eea 0%, #f093fb 100%);
          border-radius: 2px;
        }
      }

      .segments-info {
        text-align: center;
        margin-bottom: 25px;

        .info-label {
          color: #868e96;
          font-size: 13px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: rgba(134, 142, 150, 0.1);
          border-radius: 20px;

          i {
            color: #667eea;
          }
        }
      }

      .segments-timeline {
        display: flex;
        flex-direction: column;
        gap: 15px;

        .segment-item-combined {
          display: flex;
          align-items: center;
          position: relative;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border: 2px solid #e9ecef;
          border-radius: 50px;
          padding: 4px;
          transition: all 0.3s ease;

          &:hover {
            border-color: #dee2e6;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
            transform: translateX(5px);
          }

          .segment-left,
          .segment-right {
            flex: 1;
            padding: 12px 20px;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            gap: 10px;
            color: white;
            font-weight: 600;
            font-size: 14px;
            border-radius: 45px;
            position: relative;
            overflow: hidden;

            &:before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(255, 255, 255, 0);
              transition: background 0.3s ease;
            }

            i {
              font-size: 18px;
              z-index: 1;
            }

            span {
              z-index: 1;
              white-space: nowrap;
            }

            &.disabled {
              opacity: 0.5;
              cursor: not-allowed;
              background: #dee2e6 !important;

              &:hover {
                transform: none;
              }
            }

            &:hover:not(.disabled) {
              transform: scale(1.02);

              &:before {
                background: rgba(255, 255, 255, 0.1);
              }
            }

            &.active {
              box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2);

              &:before {
                background: rgba(0, 0, 0, 0.1);
              }
            }
          }

          .segment-left {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            justify-content: flex-start;

            &.active {
              background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
            }
          }

          .segment-right {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            justify-content: flex-end;
            flex-direction: row-reverse;

            &.active {
              background: linear-gradient(135deg, #f5576c 0%, #f093fb 100%);
            }
          }

          .segment-divider {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            z-index: 10;
            background: white;
            border-radius: 50%;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

            .vs-label {
              font-size: 10px;
              font-weight: bold;
              color: #868e96;
              text-transform: uppercase;
            }
          }

          .match-badge {
            position: absolute;
            top: -8px;
            right: 20px;
            background: linear-gradient(135deg, #48c774 0%, #3ec46d 100%);
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(72, 199, 116, 0.4);
            z-index: 11;

            i {
              font-size: 14px;
              color: white;
            }
          }
        }
      }
    }

    .player-section {
      background: #f8f9fa;
      padding: 25px;
      border-radius: 12px;

      h4 {
        margin: 0 0 20px 0;
        color: #495057;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 1px;
      }

      .player-wrapper {
        margin-bottom: 25px;
        padding: 20px;
        background: white;
        border-radius: 10px;
        border: 1px solid #e9ecef;

        h5 {
          margin: 0 0 15px 0;
          color: #495057;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: flex;
          align-items: center;
          gap: 8px;

          i {
            color: #667eea;
            font-size: 16px;
          }
        }

        &.duplicate-player {
          background: linear-gradient(135deg, #fff5f8 0%, #ffeef2 100%);
          border-color: #f8d7da;

          h5 {
            i {
              color: #f093fb;
            }
          }
        }
      }

      .player-controls {
        margin-bottom: 20px;

        .el-button {
          padding: 12px 24px;
          font-weight: 600;

          i {
            margin-right: 5px;
          }
        }
      }

      .segment-highlight {
        margin-top: 15px;
        text-align: center;

        .el-tag {
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
        }
      }
    }
  }
}

::v-deep .duplicate-modal {
  .duplicate-content {
    .segments-section {
      margin-bottom: 20px;

      h4 {
        margin: 0 0 12px 0;
        color: #495057;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: center;
        position: relative;

        &:after {
          content: '';
          position: absolute;
          bottom: -6px;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 2px;
          background: linear-gradient(135deg, #667eea 0%, #f093fb 100%);
          border-radius: 1px;
        }
      }

      .segments-info {
        text-align: center;
        margin-bottom: 15px;

        .info-label {
          color: #868e96;
          font-size: 11px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px 10px;
          background: rgba(134, 142, 150, 0.08);
          border-radius: 15px;

          i {
            color: #667eea;
            font-size: 12px;
          }
        }
      }

      .segments-timeline {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;

        .segment-item-combined {
          display: flex;
          align-items: center;
          position: relative;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border: 1px solid #e9ecef;
          border-radius: 20px;
          padding: 2px;
          transition: all 0.2s ease;
          width: 180px;
          flex-shrink: 0;

          &:hover {
            border-color: #dee2e6;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
            transform: translateY(-2px);
          }

          .segment-left,
          .segment-right {
            flex: 1;
            padding: 5px 10px;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 5px;
            color: white;
            font-weight: 600;
            font-size: 11px;
            border-radius: 18px;
            position: relative;
            overflow: hidden;
            min-height: 28px;

            &:before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(255, 255, 255, 0);
              transition: background 0.2s ease;
            }

            i {
              font-size: 14px;
              z-index: 1;
            }

            span {
              z-index: 1;
              white-space: nowrap;
              font-size: 11px;
            }

            &.disabled {
              opacity: 0.5;
              cursor: not-allowed;
              background: #dee2e6 !important;

              &:hover {
                transform: none;
              }
            }

            &:hover:not(.disabled) {
              &:before {
                background: rgba(255, 255, 255, 0.1);
              }
            }

            &.active {
              box-shadow: inset 0 1px 4px rgba(0, 0, 0, 0.15);

              &:before {
                background: rgba(0, 0, 0, 0.08);
              }
            }
          }

          .segment-left {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            justify-content: flex-start;

            &.active {
              background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
            }
          }

          .segment-right {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            justify-content: flex-end;
            flex-direction: row-reverse;

            &.active {
              background: linear-gradient(135deg, #f5576c 0%, #f093fb 100%);
            }
          }

          .segment-divider {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            z-index: 10;
            background: white;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

            .vs-label {
              font-size: 8px;
              font-weight: bold;
              color: #868e96;
              text-transform: uppercase;
            }
          }

          .match-badge {
            position: absolute;
            top: -6px;
            right: 10px;
            background: linear-gradient(135deg, #48c774 0%, #3ec46d 100%);
            border-radius: 50%;
            width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 1px 4px rgba(72, 199, 116, 0.3);
            z-index: 11;

            i {
              font-size: 10px;
              color: white;
            }
          }
        }
      }
    }
  }
}

@keyframes progress {
  0% {
    stroke-dasharray: 0 100;
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>